import { Stack, StackProps, Typography, TypographyProps } from '@mui/material';
import { useMemo } from 'react';

interface NamedValueProps {
  name: string;
  value: string | number | boolean;
  keyColor?: TypographyProps['color'];
  valueColor?: TypographyProps['color'];
  direction?: StackProps['direction'];
}

export default function KeyValue({ name, value, keyColor, valueColor, direction }: NamedValueProps): React.JSX.Element {
  const isHeadlined = useMemo(() => !direction || direction === 'column', [direction]);
  const maxValueLength = 50;

  const trimmedValue = useMemo(() => {
    const valueString = value.toString();
    const shortValue = valueString.slice(0, maxValueLength);
    const appendix = valueString.length > maxValueLength ? '...' : '';

    return shortValue + appendix;
  }, [maxValueLength, value]);

  const valueElement = useMemo(
    () => (
      <Typography noWrap={!isHeadlined} color={valueColor ?? 'textPrimary'}>
        {trimmedValue}
      </Typography>
    ),
    [trimmedValue, valueColor],
  );

  const keyElement = useMemo(
    () => (
      <Typography noWrap={isHeadlined} color={keyColor ?? 'textSecondary'} fontWeight={700}>
        {name + (isHeadlined ? '' : ':')}
      </Typography>
    ),
    [name, keyColor, isHeadlined],
  );

  return (
    <Stack direction={isHeadlined ? 'column' : 'row'} spacing={isHeadlined ? 0 : 1}>
      {keyElement}
      {valueElement}
    </Stack>
  );
}
